import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import styled from 'styled-components';
import { isValidRole, VALID_ROLES } from '../constants/roles';

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 50vh;
  color: #8B5A96;
`;

const EmailVerificationPrompt = styled.div`
  max-width: 500px;
  margin: 3rem auto;
  padding: 2rem;
  background-color: #fff3cd;
  color: #856404;
  border-radius: 10px;
  text-align: center;
`;

const ResendButton = styled.button`
  background: linear-gradient(135deg, #8B5A96, #A569BD);
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 20px;
  cursor: pointer;
  margin-top: 1rem;
  font-weight: 500;
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ProtectedRoute = ({ 
  children, 
  requireRole = null, 
  requireEmailVerification = true,
  redirectTo = "/login" 
}) => {
  const { user, profile, loading, isEmailVerified, resendConfirmation, hasRole } = useAuth();
  const [resending, setResending] = React.useState(false);

  // Validate role parameter
  React.useEffect(() => {
    if (requireRole && !isValidRole(requireRole)) {
      console.error(`ProtectedRoute: Invalid requireRole '${requireRole}'. Must be one of: ${VALID_ROLES.join(', ')}.`);
    }
  }, [requireRole]);

  if (loading) {
    return (
      <LoadingContainer>
        <h3>Loading...</h3>
      </LoadingContainer>
    );
  }

  // Not authenticated
  if (!user || !profile) {
    return <Navigate to={redirectTo} state={{ from: window.location.pathname }} replace />;
  }

  // Check role requirement
  if (requireRole && !hasRole(requireRole)) {
    return <Navigate to="/unauthorized" replace />;
  }

  const handleResendConfirmation = async () => {
    setResending(true);
    try {
      await resendConfirmation(user.email);
      alert('Confirmation email sent! Please check your inbox.');
    } catch (error) {
      alert('Failed to send confirmation email. Please try again.');
    } finally {
      setResending(false);
    }
  };

  // Check email verification requirement
  if (requireEmailVerification && !isEmailVerified()) {
    return (
      <EmailVerificationPrompt>
        <h2>Email Verification Required</h2>
        <p>
          Please verify your email address to access this feature. 
          Check your inbox for a confirmation email.
        </p>
        <p>
          <strong>Email:</strong> {user.email}
        </p>
        <ResendButton 
          onClick={handleResendConfirmation}
          disabled={resending}
        >
          {resending ? 'Sending...' : 'Resend Confirmation Email'}
        </ResendButton>
      </EmailVerificationPrompt>
    );
  }

  return children;
};

export default ProtectedRoute;